import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { NG_BOOTSTRAP_OPEN_DELAY } from '@app/core/models/constants/app.constants';
import { SurfaceTier } from '@app/core/models/types/surface-tier.type';
import { BigCommerceLinkService } from '@app/core/services/bigcommerce-link.service';
import { FdxUtilsService } from '@app/core/services/fdx-utils.service';
import { SurfaceTierThemeService, SurfaceTierThemeType } from '@app/core/services/surface-tier-theme.service';
import { SurfaceUpgradeFeatureProp, SurfaceUpgradeFeatureService, SurfaceUpgradeFeatureType, TRY_IT_OUT } from '@app/core/services/surface-upgrade-feature.service';
import { ExportInterface } from '@app/exports/models/interfaces/export.interface';
import { SurfacePopoverStateService } from '@app/modules/surface/services/surface-popover-state.service';
import { faArrowUpRightFromSquare, faXmarkLarge, IconDefinition } from '@fortawesome/pro-solid-svg-icons';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { PlacementArray } from '@ng-bootstrap/ng-bootstrap/util/positioning';
import { hide, Options } from '@popperjs/core';
import { skip, Subject, take, takeUntil, tap } from 'rxjs';

const STICKY_TAGS: string[] = ['fdx-app-vertical-menu', 'fdx-page-header', 'fdx-drawer'];
@Component({
    selector: 'fdx-surface-upgrade-popover',
    templateUrl: './surface-upgrade-popover.component.html',
    styleUrl: './surface-upgrade-popover.component.scss',
    standalone: false
})
export class SurfaceUpgradePopoverComponent implements OnChanges, OnInit, OnDestroy, AfterViewInit {
    private readonly unsubscribe$: Subject<void> = new Subject<void>();

    @Input() target: HTMLElement;
    @Input() feature: SurfaceUpgradeFeatureProp;
    /**
     * Which tier to use for this popover. Defaults to 'retail'.
     */
    @Input() tier: SurfaceTier = 'retail';
    @Input() placement: PlacementArray = 'auto';
    /**
     * Required if showing the products button that should link to the channel assignments page
     */
    @Input() currentExport?: Partial<ExportInterface>;
    /**
     * Another way to pass in an export ID for remembering dismissed popovers
     */
    @Input() exportId?: string;

    @ViewChild('popover') readonly popover?: NgbPopover;
    @Output() readonly popoverInitialized: EventEmitter<NgbPopover> = new EventEmitter<NgbPopover>();
    @Output() readonly popoverShown: EventEmitter<void> = new EventEmitter<void>();
    @Output() readonly popoverHidden: EventEmitter<void> = new EventEmitter<void>();

    // eslint-disable-next-line @typescript-eslint/typedef
    rememberForm = new FormGroup({
        dontShowAgain: new FormControl<boolean>(false)
    });

    tierThemeType: SurfaceTierThemeType;
    featureType: SurfaceUpgradeFeatureType;

    /**
     * Gets set if this popover is inside a sticky parent (like the vertical nav menu or a header)
     */
    stickyParent: HTMLElement;
    /**
     * The old position style of the target
     */
    oldPosition: string;
    productsUrl: string;

    readonly linkIcon: IconDefinition = faArrowUpRightFromSquare;
    readonly closeIcon: IconDefinition = faXmarkLarge;

    get isPopoverOpen(): boolean {
        return this.popover?.isOpen();
    }

    get popoverClass(): string {
        return `surface-upgrade-popover-${this.featureType?.popupOrientation}`;
    }

    /**
     * If we have a products button, the primary button should use the Surface tier theme.
     * Otherwise, use the outline variant of the FDX theme.
     */
    get planButtonClass(): string {
        if (this.featureType?.productsButtonText) {
            return `btn-${this.tierThemeType?.tier}`;
        }
        return `btn-outline-${this.tierThemeType?.fdxBtnTheme}`;
    }

    get productsButtonClass(): string {
        return `btn-outline-${this.tierThemeType?.fdxBtnTheme}`;
    }

    get backgroundTheme(): string {
        return `bg-${this.tierThemeType?.tier}`;
    }

    get titleHTML(): string {
        if (!this.featureType || !this.tierThemeType) {
            return '';
        }
        return this.surfaceUpgradeFeatureService.getTitleHTML(this.featureType, this.tierThemeType);
    }

    get descriptionHTML(): string {
        if (!this.featureType) {
            return '';
        }
        return this.surfaceUpgradeFeatureService.getDescriptionHTML(this.featureType, this.surfaceTierThemeService.getCurrentTierThemeType());
    }

    get plansButtonText(): string {
        if (!this.featureType) {
            return TRY_IT_OUT;
        }
        return this.surfaceUpgradeFeatureService.getPlansButtonText(this.featureType);
    }

    constructor(
        protected readonly bigCommerceLinkService: BigCommerceLinkService,
        private readonly fdxUtils: FdxUtilsService,
        private readonly surfacePopoverStateService: SurfacePopoverStateService,
        protected readonly surfaceUpgradeFeatureService: SurfaceUpgradeFeatureService,
        protected readonly surfaceTierThemeService: SurfaceTierThemeService
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if ('feature' in changes) {
            this.featureType = this.surfaceUpgradeFeatureService.getUpgradeFeatureType(this.feature);
        }

        if ('tier' in changes) {
            this.tierThemeType = this.surfaceTierThemeService.getTierThemeType(this.tier);
        }

        if ('target' in changes && this.target) {
            this.checkForStickyParent();
        }
    }

    ngOnInit(): void {
        // Init tier with potential default value
        this.tierThemeType = this.surfaceTierThemeService.getTierThemeType(this.tier);

        this.bindPopoverStateService();

        this.bigCommerceLinkService.getProductsUrlForChannel(this.currentExport)
            .pipe(
                take(1),
                tap((url) => (this.productsUrl = url)),
                takeUntil(this.unsubscribe$)
            ).subscribe();
    }

    ngAfterViewInit(): void {
        if (this.popover) {
            this.popoverInitialized.emit(this.popover);
        }
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    private checkForStickyParent(): void {
        STICKY_TAGS.some((tag) => {
            const sticky: HTMLElement = this.target.closest(tag);
            if (sticky) {
                this.stickyParent = sticky;
                return true;
            }
            return false;
        });
    }

    private bindPopoverStateService(): void {
        this.surfacePopoverStateService.isBackdropOpen$.pipe(
            skip(1), // Skip the initial value
            tap((backdropOpen: boolean) => {
                if (!backdropOpen && this.isPopoverOpen) {
                    this.popover.close();
                }
            }),
            takeUntil(this.unsubscribe$)
        ).subscribe();
    }

    popperOptions: (options: Partial<Options>) => Partial<Options> = (options: Partial<Options>) => {
        return {
            ...options,
            modifiers: [ ...options.modifiers, hide ]
        }
    }

    /**
     * Public so it can be invoked by parent components.
     * Initializes the backdrop and opens the popover.
     */
    invokePopover(): void {
        if (this.isPopoverOpen) {
            this.onPopoverHidden();
        } else {
            this.popover.open();
            this.surfacePopoverStateService.registerPopover(this.popover, this.stickyParent);

            this.updateIndexStyling();
        }
    }

    onPopoverShown(): void {
        this.popoverShown.emit();
    }

    onPopoverHidden(): void {
        this.surfacePopoverStateService.unregisterPopover(this.popover);
        this.resetIndexStyling();

        const exportIdToUse = this.exportId ?? this.currentExport?.id;
        if (this.rememberForm.value.dontShowAgain && exportIdToUse) {
            this.surfaceUpgradeFeatureService.rememberFeaturePopoverDismissed(exportIdToUse, this.feature);
        }

        this.popoverHidden.emit();
    }

    private updateIndexStyling(): void {
        this.target.style['z-index'] = '1070'; // Bootstrap popover z-index
        this.oldPosition = this.target.style['position'];
        if (this.oldPosition !== 'relative') {
            this.target.style['position'] = 'relative';
        }
        this.stickyParent?.classList.add('fdx-show-above-modal');
    }

    private resetIndexStyling(): void {
        this.target.style['z-index'] = 'unset';
        this.target.style['position'] = this.oldPosition;

        if (this.fdxUtils.hasReducedMotion) {
            this.removeStickyParentClass();
        } else {
            setTimeout(() => {
                this.removeStickyParentClass();
            }, NG_BOOTSTRAP_OPEN_DELAY);
        }
    }

    private removeStickyParentClass(): void {
        // Only reset stickyParent class if there are no other popovers in the same sticky parent
        if (!this.surfacePopoverStateService.hasOtherPopoversInStickyParent(this.stickyParent)) {
            this.stickyParent?.classList.remove('fdx-show-above-modal');
        }
    }
}
